import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Session } from '../utils/session';
import { API_BASE_URL } from '../../config';
const authHeaders = () => {
  const token = Session.getToken();

  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`
  };
};

export default function TeamsTable() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  // Date formatting utility
  const formatDate = useCallback((raw) => {
    if (!raw) return '';
    let date = new Date(raw);
    if (isNaN(date.getTime())) {
      const msMatch = String(raw).match(/\/(?:Date)?\((\d+)\)\//i) || String(raw).match(/^(\d{10,})$/);
      if (msMatch) {
        const ts = Number(msMatch[1]);
        date = new Date(ts < 1e12 ? ts * 1000 : ts);
      }
    }
    if (isNaN(date.getTime())) return String(raw);
    const dd = String(date.getDate()).padStart(2, '0');
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const yyyy = date.getFullYear();
    return `${dd}/${mm}/${yyyy}`;
  }, []);

  useEffect(() => {
    let ignore = false;
    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await fetch(`${API_BASE_URL}/api/Workspace/Workspaces_List`, {
          headers: authHeaders()  // ✅ include authorization header
        });

        if (!response.ok) throw new Error(`Request failed with status ${response.status}`);

        const data = await response.json();

        if (ignore) return;
        setRows(Array.isArray(data) ? data : []);
      } catch (err) {
        if (!ignore) {
          console.error('Error loading workspaces:', err);
          setError(err.message || 'Something went wrong');
        }
      } finally {
        if (!ignore) setLoading(false);
      }
    };
    loadData();
    return () => { ignore = true; };
  }, []);

  // Memoize normalized rows
  const normalizedRows = useMemo(() => {
    return rows.map((w) => ({
      WorkspaceID: w.WorkspaceID ?? w.workspaceID ?? w.workspaceId ?? w.id,
      WorkspaceName: w.WorkspaceName ?? w.workspaceName ?? w.name ?? '',
      Description: w.Description ?? w.description ?? '',
      CreatedAt: formatDate(w.createdAt ?? w.CreatedAt ?? w.created_at ?? ''),
      IsActive: w.IsActive ?? w.isActive ?? false,
      _raw: w
    }));
  }, [rows, formatDate]);

  const handleEdit = useCallback((workspace) => {
    navigate(`/teamsid/${workspace.WorkspaceID}`, { state: { workspace } });
  }, [navigate]);

  if (loading) {
    return (
      <div className="table-responsive">
        <div className="p-3 text-center text-muted">Loading teams...</div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="table-responsive">
        <div className="p-3 text-center text-danger">Error: {error}</div>
      </div>
    );
  }

  return (
    <div className="table-responsive">
      <table className="table table-custom table-hover">
        <thead>
          <tr>
            <th>Name</th>
            <th>Added On</th>
            <th>Status</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          {normalizedRows.length === 0 && (
            <tr>
              <td colSpan={4} className="text-center text-muted py-3">
                No teams found.
              </td>
            </tr>
          )}
          {normalizedRows.map((workspace) => (
            <tr key={workspace.WorkspaceID}>
              <td>
                <div className="employee-info">
                  <div className="emp-text">
                    <h6>{workspace.WorkspaceName}</h6>
                  </div>
                </div>
              </td>
              <td>{workspace.CreatedAt}</td>
              <td>{workspace.IsActive ? 'Active' : 'Inactive'}</td>
              <td>
                <i
                  className="far fa-edit"
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleEdit(workspace)}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
